import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LoadingControllerWrapperService } from 'src/app/services/loading-controller-wrapper/loading-controller-wrapper.service';
import { PermissionCheckerService } from 'src/app/services/permission-checker/permission-checker.service';
import { QuoteService } from 'src/app/services/quote/quote.service';
import { ToastControllerWrapperService } from 'src/app/services/toast-controller-wrapper/toast-controller-wrapper.service';
import { finalize } from 'rxjs/operators';
import { ReturnMessage } from 'src/app/models/return-message';
import { ReturnQuoteQuestionsPayload } from 'src/app/models/quote/return-quote-questions-payload';
import { ReturnQuoteQuestion } from 'src/app/models/quote/return-quote-question';
import { QuoteQuestionsNavigationState } from 'src/app/models/quote/quote-questions-navigation-state';
import { Title } from '@angular/platform-browser';
import { NavController } from '@ionic/angular';
import { FormArray } from '@angular/forms';
import { environment } from 'src/environments/environment';
import { PhoneNumberFormat } from 'src/app/models/customer/phone-number-format';
import { FormatService } from 'src/app/services/format/format.service';

@Component({
  selector: 'app-quote-questions',
  templateUrl: './quote-questions.page.html',
  styleUrls: ['./quote-questions.page.scss'],
})
export class QuoteQuestionsPage implements OnInit {

  title: string = 'Quote Questions';
  quoteQuestions: ReturnQuoteQuestion[] = null;
  disableSubmitButton: boolean;
  canLeave: boolean;
  canCRUDQuotes: boolean;
  form: FormGroup = new FormGroup({});
  navigationState: QuoteQuestionsNavigationState;

  maxLengths: any = {
    'Text': 30,
    'Number': 25,
    'Purchase_Order': 15,
    'Email': 40,
    'Default': 255
  };

  DFLT_FIELD_CONF_NAME = 'Default';
  PHONE_NBR_DATA_TYPE = 'PhoneNumber';
  PURCHASE_ORDER_FIELD = 'Purchase_Order';
  CALLER_PHONE_NUMBER = 'Caller_Phone';

  fieldsConfiguration: any = {
    'Text': {
      validationMessages: [
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Text}.` }
      ],
      validators: [Validators.maxLength(this.maxLengths.Text)],
      inputMode: 'text'
    },
    'Purchase_Order': {
      validationMessages: [
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Purchase_Order}.` }
      ],
      validators: [Validators.maxLength(this.maxLengths.Purchase_Order)],
      inputMode: 'text'
    },
    'Number': {
      validationMessages: [
        { name: 'pattern', message: 'This field only accepts numeric values.' },
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Number}.` }
      ],
      validators: [Validators.pattern(environment.NUMBER_REGEX), Validators.maxLength(this.maxLengths.Number)],
      inputMode: 'number'
    },
    'Email': {
      validationMessages: [
        { name: 'pattern', message: 'This field needs to be a valid email address.' },
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Email}.` }
      ],
      validators: [Validators.pattern(environment.EMAIL_ADDRESS_REGEX), Validators.maxLength(this.maxLengths.Email)],
      inputMode: 'email'
    },
    'Default': {
      validationMessages: [
        { name: 'maxlength', message: `This field max length is ${this.maxLengths.Default}.` }
      ],
      validators: [Validators.maxLength(this.maxLengths.Default)],
      inputMode: 'text'
    }

  };

  format: PhoneNumberFormat;
  phonePlaceholder: string;
  phoneNumberMask: (string | RegExp)[];

  get questions() {
    return this.form.get('questions') as FormArray;
  }

  get shouldRenderContent() {
    return this.quoteQuestions != null && this.questions != null;
  }

  get questionsExist() {
    return this.form && this.quoteQuestions.length > 0;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private navController: NavController,
    private titleService: Title,
    private loadingWrapper: LoadingControllerWrapperService,
    private toastWrapper: ToastControllerWrapperService,
    private permissionChecker: PermissionCheckerService,
    private quoteService: QuoteService,
    private formatService: FormatService,
  ) { }

  ngOnInit() {
    this.navigationState = <QuoteQuestionsNavigationState>this.router.getCurrentNavigation()?.extras.state;
    this.disableSubmitButton = true;
    this.canLeave = false;
  }

  async ionViewWillEnter() {
    this.titleService.setTitle(this.title);
    if (!this.navigationState) {
      this.navController.navigateRoot(['']);
      return;
    }

    await this.loadingWrapper.presentLoading('');
    forkJoin([
      this.route.snapshot.data.phoneNumberFormat,
      this.permissionChecker.canCRUDQuotes(),
      this.quoteService.getQuoteQuestions(this.navigationState.quoteId)
    ])
      .pipe(finalize(async () => await this.loadingWrapper.dismissLoading()))
      .subscribe(([phoneNumberFormat, canCRUDQuotes, getQuoteQuestions]: [PhoneNumberFormat, boolean, ReturnMessage<ReturnQuoteQuestionsPayload>]) => {

        this.format = phoneNumberFormat;
        this.phonePlaceholder = this.formatService.createPhonePlaceHolder(this.format);
        this.phoneNumberMask = this.formatService.createPhoneNumberMask(this.format);

        this.canCRUDQuotes = canCRUDQuotes;
        this.quoteQuestions = getQuoteQuestions.success ? getQuoteQuestions.payload.questions : [];
        if (!this.quoteQuestions || this.quoteQuestions.length === 0) {
          this.disableSubmitButton = true;
          this.canLeave = true;
        } else {
          this.disableSubmitButton = false;
          this.canLeave = false;
        }
        this.buildForm();
      },
        error => {
          // the api returns HTTP 400 when there is no questions instead of an empty list...
          this.quoteQuestions = [];
        });
  }

  buildForm() {
    this.form = this.fb.group({
      questions: this.fb.array([])
    });

    for (const question of this.quoteQuestions) {
      let fieldConfiguration = this.getFieldConfigurationByQuestion(question);
      if (fieldConfiguration) {
        let questionInitialValue = question.value;

        // The field caller phone number comes unformatted from the database so we need to apply the format first
        if (question.userDefinedField == this.CALLER_PHONE_NUMBER) {
          questionInitialValue = this.formatService.applyPhoneNumberFormat(this.format.phoneNumberFormat, question.value);
        }

        this.addQuestion(this.fb.control(questionInitialValue || '', fieldConfiguration.validators));
      }
    }
  }

  getFieldConfigurationByQuestion(question: ReturnQuoteQuestion) {
    let dfltConf = this.fieldsConfiguration[this.DFLT_FIELD_CONF_NAME];
    if (!question)
      return dfltConf;

    if (question.userDefinedField == this.PURCHASE_ORDER_FIELD) {
      return this.fieldsConfiguration[this.PURCHASE_ORDER_FIELD];
    }

    return this.fieldsConfiguration[question.dataType] || dfltConf;
  }

  getFieldConfigurationByQuestionIndex(index: number) {
    return this.getFieldConfigurationByQuestion(this.quoteQuestions[index]);
  }

  addQuestion(question: FormControl) {
    this.questions.push(question);
  }

  getQuestionDescription(index: number) {
    return this.quoteQuestions[index] ? this.quoteQuestions[index].question : '';
  }

  getQuestionDataType(index: number) {
    return this.quoteQuestions[index] && this.quoteQuestions[index].dataType ? this.quoteQuestions[index].dataType : this.DFLT_FIELD_CONF_NAME;
  }

  getValidationMessages(index: number) {
    let fieldConfiguration = this.getFieldConfigurationByQuestionIndex(index);
    return fieldConfiguration.validationMessages;
  }

  getInputMode(index: number) {
    let fieldConfiguration = this.getFieldConfigurationByQuestionIndex(index);
    return fieldConfiguration.inputMode;
  }

  addPhoneNumberMissingZeros(formattedPhoneNumber: string) {
    // unapply/apply adds missing zeros at the end
    let unformattedPhoneNumber = this.formatService.unapplyPhoneNumberFormat(this.format.phoneNumberFormat, formattedPhoneNumber);
    let reformattedPhoneNumber = this.formatService.applyPhoneNumberFormat(this.format.phoneNumberFormat, unformattedPhoneNumber);
    return reformattedPhoneNumber;
  }

  UpdatePhoneNumber(post: any, i: number) {
    if (this.quoteQuestions[i].userDefinedField == this.CALLER_PHONE_NUMBER) {
      // If the field is caller phone number we need to send back the UNFORMATTED phone number
      let formattedPhoneNumber = this.form.value.questions[i];
      let unformattedPhoneNumber = this.formatService.unapplyPhoneNumberFormat(this.format.phoneNumberFormat, formattedPhoneNumber);
      post.questionsAnswers.push({ id: this.quoteQuestions[i].id, value: unformattedPhoneNumber });
    }
    else {
      // for user defined fields with phone data type we need to send the FORMATTED phone number
      let formattedPhoneNumber = this.addPhoneNumberMissingZeros(this.form.value.questions[i]);
      post.questionsAnswers.push({ id: this.quoteQuestions[i].id, value: formattedPhoneNumber });
    }
  }

  async submit() {

    if (this.form.valid) {
      this.canLeave = true;

      await this.toastWrapper.dismissToast();
      await this.loadingWrapper.presentLoading('');

      let post = { questionsAnswers: [] };

      for (var i = 0; i < this.quoteQuestions.length; i++) {
        if (this.quoteQuestions[i].dataType == this.PHONE_NBR_DATA_TYPE) {
          this.UpdatePhoneNumber(post, i);
        }
        else {
          post.questionsAnswers.push({ id: this.quoteQuestions[i].id, value: this.form.value.questions[i] });
        }
      }

      this.quoteService
        .updateQuoteQuestions(post)
        .pipe(
          finalize(async () => {
            await this.loadingWrapper.dismissLoading();
          })
        )
        .subscribe((response) => {
          if (response.success) {
            this.router.navigate(['..'], { relativeTo: this.route });
          }
        });
    } else {
      await this.toastWrapper.dismissToast();
      await this.toastWrapper.presentErrorToast('Please complete the form');
    }
  }

}
